import {
  ActionIcon,
  Alert,
  Button,
  Card,
  FileButton,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconFlask,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconAlertCircle,
} from "@/lib/icons";
import { Eye } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { ApiError } from "@/lib/api/client";
import {
  type LabTemplate,
  deleteLabTemplate,
  listLabTemplates,
  uploadLabTemplate,
} from "@/lib/api/laboratory";
import { formatDate } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

export function LabTemplatesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasPermission("reference.update"));
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [search, setSearch] = useState("");
  const [previewing, setPreviewing] = useState<LabTemplate | null>(null);
  const [previewOpen, previewHandlers] = useDisclosure(false);
  const [deleting, setDeleting] = useState<LabTemplate | null>(null);
  const [deleteOpen, deleteHandlers] = useDisclosure(false);

  const { data, isLoading } = useQuery({
    queryKey: ["lab-templates"],
    queryFn: listLabTemplates,
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file || !name.trim()) throw new Error("missing");
      return uploadLabTemplate(name.trim(), file, description.trim() || undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-templates"] });
      notifications.show({ color: "green", message: t("lab_templates.uploaded") });
      setName("");
      setDescription("");
      setFile(null);
    },
    onError: (e) => {
      const msg =
        e instanceof ApiError
          ? e.message
          : t("lab_templates.upload_failed");
      notifications.show({ color: "red", message: msg });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLabTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-templates"] });
      notifications.show({ color: "green", message: t("lab_templates.deleted") });
      deleteHandlers.close();
      setDeleting(null);
    },
  });

  const filtered = (data ?? []).filter((tpl) =>
    !search.trim() || tpl.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("lab_templates.title")}
        subtitle={data ? `${data.length}` : undefined}
        back
      />

      {canEdit && (
        <Card padding="lg" radius="xl">
          <Title order={5} fw={700} mb="md">
            {t("lab_templates.upload_title")}
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            {t("lab_templates.upload_hint")}
          </Text>
          <Stack gap="md">
            <TextInput
              label={t("lab_templates.field_name")}
              placeholder={t("lab_templates.field_name_placeholder")}
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <TextInput
              label={t("lab_templates.field_description")}
              placeholder={t("lab_templates.field_description_placeholder")}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />
            <Group>
              <FileButton
                onChange={setFile}
                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              >
                {(props) => (
                  <Button
                    {...props}
                    variant="light"
                    radius="xl"
                    leftSection={<IconUpload size={16} strokeWidth={2.2} />}
                  >
                    {file ? file.name : t("lab_templates.pick_file")}
                  </Button>
                )}
              </FileButton>
              <Button
                radius="xl"
                disabled={!file || !name.trim()}
                loading={uploadMutation.isPending}
                onClick={() => uploadMutation.mutate()}
              >
                {t("lab_templates.upload_button")}
              </Button>
            </Group>
            {uploadMutation.error && (
              <Alert
                color="red"
                icon={<IconAlertCircle size={16} />}
                radius="lg"
              >
                {uploadMutation.error instanceof ApiError
                  ? uploadMutation.error.message
                  : t("lab_templates.upload_failed")}
              </Alert>
            )}
          </Stack>
        </Card>
      )}

      <Card padding="md" radius="xl">
        <TextInput
          rightSection={<IconSearch size={18} strokeWidth={2} />}
          placeholder={t("lab_templates.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={680}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("lab_templates.field_name")}</Table.Th>
                <Table.Th style={{ width: 100 }}>{t("lab_templates.row_count")}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("examinations.fields.created_at")}</Table.Th>
                <Table.Th style={{ width: 120 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <EmptyState
                      icon={<IconFlask size={32} strokeWidth={1.5} />}
                      title={t("lab_templates.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.map((tpl) => (
                <Table.Tr key={tpl.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm" fw={600}>{tpl.name}</Text>
                      {tpl.description && (
                        <Text size="xs" c="dimmed">{tpl.description}</Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600} c="brand.7">
                      {tpl.data_rows.length}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{formatDate(tpl.created_at)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <ActionIcon
                        variant="subtle"
                        color="brand"
                        onClick={() => {
                          setPreviewing(tpl);
                          previewHandlers.open();
                        }}
                        aria-label="preview"
                      >
                        <Eye size={16} strokeWidth={1.8} />
                      </ActionIcon>
                      {canEdit && (
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            setDeleting(tpl);
                            deleteHandlers.open();
                          }}
                          aria-label="delete"
                        >
                          <IconTrash size={16} strokeWidth={1.8} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal
        opened={previewOpen}
        onClose={() => {
          previewHandlers.close();
          setPreviewing(null);
        }}
        title={previewing?.name}
        size="xl"
        radius="lg"
      >
        {previewing && (
          <Stack>
            {previewing.description && (
              <Text size="sm" c="dimmed">{previewing.description}</Text>
            )}
            <Table.ScrollContainer minWidth={500}>
              <Table verticalSpacing="xs" striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    {previewing.headers.map((h, i) => (
                      <Table.Th
                        key={i}
                        style={
                          i === previewing.result_col_index
                            ? { background: "var(--mantine-color-yellow-0)" }
                            : undefined
                        }
                      >
                        {h}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {previewing.data_rows.map((row, ri) => (
                    <Table.Tr key={ri}>
                      {row.map((cell, ci) => (
                        <Table.Td
                          key={ci}
                          style={
                            ci === previewing.result_col_index
                              ? { background: "var(--mantine-color-yellow-0)" }
                              : undefined
                          }
                        >
                          <Text size="xs" style={{ whiteSpace: "pre-line" }}>
                            {cell || (ci === previewing.result_col_index ? "—" : "")}
                          </Text>
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={deleteOpen}
        onClose={() => {
          deleteHandlers.close();
          setDeleting(null);
        }}
        title={t("lab_templates.delete_title")}
        size="sm"
        radius="lg"
      >
        <Stack>
          <Text size="sm">
            {t("lab_templates.delete_confirm", { name: deleting?.name ?? "" })}
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => {
                deleteHandlers.close();
                setDeleting(null);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              leftSection={<IconTrash size={16} strokeWidth={2.2} />}
              onClick={() => deleting && deleteMutation.mutate(deleting.id)}
            >
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
